// @ts-strict-ignore
import React from 'react';
import Space from 'antd/lib/space';
import {
    AppComponentProps,
    QuickStartCISetupStep,
    QuickStartCISetupStepType,
    QuickStartCISetupViewProps,
} from '../../../typescriptHelpers';
import { InfoTip, setupModuleStyle } from '../../setup/shared';
import { SettingOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { QuestionsInfoSection } from '../QuestionsInfoSection';
import { ScanList } from '../ScanList';
import Form from 'antd/lib/form';
import Button from 'antd/lib/button';
import { QuickStartCIScanAttribute, ScanConfirmedResult, ScanResult } from '../../../types';
import { globalMessage } from '../../../utils/globalMessage';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectQuickStartCIStepData, selectAppPlatform } from '../../../selectors/selectors';
import {
    defaultOptionsForQuickStartCIScanAttribute,
    doesQuickStartCIScanAttributeAcceptMultipleValues,
    getQuickStartCIExtraAttributesForPlatform,
    gradleTaskForOutputType,
    outputAttributeNameForQuickStartCIScanAttribute,
} from '../../../helpers/displayHelpers';
import { setQuickStartCIStepData } from '../../../actions/ui';

const SetupViewComponent: React.FC<QuickStartCISetupViewProps> = ({ appId, orgId }) => {
    const [form] = Form.useForm();
    const stepData = useSelector((state: RootState) =>
        selectQuickStartCIStepData(state, appId, QuickStartCISetupStepType.ScanDetails),
    );

    const dispatch = useDispatch();

    const scanResult = stepData?.scanResult;
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const initialValues = [
        ...Object.keys(scanResult ?? {}),
        ...getQuickStartCIExtraAttributesForPlatform(platform),
    ].reduce((acc, key) => {
        const currentValue =
            defaultOptionsForQuickStartCIScanAttribute(key as QuickStartCIScanAttribute) ?? scanResult[key];

        // If the value is an array, take the first value
        const isMultivalue = doesQuickStartCIScanAttributeAcceptMultipleValues(key as QuickStartCIScanAttribute);
        let value = null;

        if (!isMultivalue && Array.isArray(currentValue)) {
            // select the first value
            value = currentValue[0];
        } else {
            value = currentValue;
        }

        acc[key] = value;
        return acc;
    }, {} as ScanResult);

    return (
        <div
            style={{
                ...setupModuleStyle,
            }}
        >
            <Form
                initialValues={initialValues}
                form={form}
                onFinish={(values) => {
                    const mappedKeysAndValues = Object.keys(values).reduce((acc, key) => {
                        let currentValue = values[key];

                        // special case to send the target as an array since we might have multiple targets in the future)
                        if (key === QuickStartCIScanAttribute.Targets) {
                            currentValue = [currentValue];
                        }

                        if (key === QuickStartCIScanAttribute.OutputType) {
                            currentValue = gradleTaskForOutputType(currentValue);
                        }

                        acc[outputAttributeNameForQuickStartCIScanAttribute(key as QuickStartCIScanAttribute)] =
                            currentValue;
                        return acc;
                    }, {} as ScanConfirmedResult);

                    dispatch(
                        setQuickStartCIStepData({
                            stepType: QuickStartCISetupStepType.ScanDetailsConfirmation,
                            appId,
                            data: {
                                scanConfirmedResult: {
                                    ...mappedKeysAndValues,
                                },
                            },
                        }),
                    );
                    globalMessage.success('Values confirmed!');
                }}
            >
                <ScanList scanResult={scanResult} appId={appId} orgId={orgId} form={form} />
                <div
                    style={{
                        paddingTop: 20,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                        {() => (
                            <Button type="primary" htmlType="submit" icon={<ArrowRightOutlined />}>
                                Save
                            </Button>
                        )}
                    </Form.Item>
                </div>
            </Form>
        </div>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <InfoTip>
                All inputs are optional; if a value isn’t specified, we’ll use a placeholder that you can manually
                replace in the final workflow files in your repo.
            </InfoTip>
            <QuestionsInfoSection />
        </Space>
    );
};

export const ScanConfirmation: QuickStartCISetupStep = {
    icon: () => SettingOutlined,
    headerTitle: 'Confirm and complete your build’s configuration.',
    SetupViewComponent,
    InfoViewComponent,
    isCompleteSelector: (state, { appId }) => {
        const stepData = selectQuickStartCIStepData(state, appId, QuickStartCISetupStepType.ScanDetailsConfirmation);
        return !!stepData?.scanConfirmedResult;
    },
};
